import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import {
  Box,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LockOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';

import MainLayout from '../../../layout/MainLayout';
import MainCard from '../../../components/MainCard';
import FormCard from '../../../components/FormCard';
import Button from '../../../components/Button';
import InputLabel from '../../../components/form/InputLabel';
import Autocomplete from '../../../components/form/Autocomplete';
import { DataGrid } from '../../../components/grid/DataGrid';

import {
  alterarFiltro,
  alterarPaginacao,
  alterarSort,
  buscarClick,
  limparFiltros,
  selecionarLog
} from '../../../reducers/logsIntegracaoPagina.reducer';
import {
  FLUXO_DOMAIN,
  TIPO_EVENTO_DOMAIN,
  SITUACAO_DOMAIN,
  buildServicoLabel,
  formatDateTime,
  formatDuracao,
  isEncryptedFormat
} from '../../../utils/logAuditoria';
import LogSituacaoBadge from './components/LogSituacaoBadge';
import LogDetalhe from './components/LogDetalhe';

// ============================================================================
// Pagina global de busca em LOG_AUDITORIA (acessivel via menu Configuracoes,
// restrita a perfil AD). UX similar ao modal mas:
//   - sem escopo de simulacao (idSimulacao e filtro como qualquer outro)
//   - aceita idOportunidade como filtro
//   - aceita datetime (data + hora) em vez de so data
//   - dispara busca apenas no clique em "Buscar"
// ============================================================================

const LogsIntegracaoPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { filtros, lista, sort, detalhe } = useSelector(
    (state) => state.logsIntegracaoPagina
  );

  const formProps = {
    ...useForm(),
    rules: {},
    initialValues: {}
  };

  const handleAlterar = (campo) => (e) => {
    const value = e?.target?.value ?? e;
    dispatch(alterarFiltro({ [campo]: value }));
  };

  const onChangeSituacao = (item) => {
    if (!item) return dispatch(alterarFiltro({ flagSuccesso: null }));
    dispatch(alterarFiltro({ flagSuccesso: item.id === 'sucesso' }));
  };

  const onChangeFluxo = (item) => {
    dispatch(alterarFiltro({ codigoFluxo: item ? [item.id] : [] }));
  };

  const onChangeTipoEvento = (item) => {
    dispatch(alterarFiltro({ tipoEvento: item ? [item.id] : [] }));
  };

  const handleBuscar = () => {
    dispatch(buscarClick());
  };

  const handleLimpar = () => {
    dispatch(limparFiltros());
  };

  const columns = useMemo(
    () => [
      {
        field: 'dataHoraInicio',
        headerName: 'Horário de execução',
        flex: 1,
        minWidth: 180,
        valueFormatter: (value) => formatDateTime(value)
      },
      {
        field: 'servico',
        headerName: 'Serviço',
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ overflow: 'hidden' }}
          >
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {buildServicoLabel(params.row)}
            </Typography>
            {(isEncryptedFormat(params.row.descricaoRequestBodyFormat) ||
              isEncryptedFormat(params.row.descricaoResponseBodyFormat)) && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: theme.palette.warning.main,
                  fontSize: 12
                }}
              >
                <LockOutlined />
              </Box>
            )}
          </Stack>
        )
      },
      {
        field: 'idOportunidade',
        headerName: 'Oportunidade',
        width: 120,
        align: 'right',
        headerAlign: 'right'
      },
      {
        field: 'idSimulacao',
        headerName: 'Simulação',
        width: 110,
        align: 'right',
        headerAlign: 'right'
      },
      {
        field: 'flagSuccesso',
        headerName: 'Situação',
        width: 140,
        renderCell: (params) => (
          <LogSituacaoBadge
            flagSuccesso={params.row.flagSuccesso}
            codigoStatusCode={params.row.codigoStatusCode}
          />
        )
      },
      {
        field: 'numeroDuracaoMs',
        headerName: 'Duração',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatDuracao(value)
      }
    ],
    [theme.palette.warning.main]
  );

  const sortModel = useMemo(
    () => [{ field: sort.field, sort: sort.direction }],
    [sort]
  );

  const paginationModel = useMemo(
    () => ({ page: lista.page - 1, pageSize: lista.pageSize }),
    [lista.page, lista.pageSize]
  );

  // Selecao atual da Situacao: precisa achar o item correspondente para
  // o Autocomplete renderizar (ele compara por idField).
  const situacaoSelecionada = useMemo(() => {
    if (filtros.flagSuccesso === true) return SITUACAO_DOMAIN[0];
    if (filtros.flagSuccesso === false) return SITUACAO_DOMAIN[1];
    return null;
  }, [filtros.flagSuccesso]);

  const fluxoSelecionado = useMemo(() => {
    const first = (filtros.codigoFluxo || [])[0];
    return first ? FLUXO_DOMAIN.find((f) => f.id === first) : null;
  }, [filtros.codigoFluxo]);

  const tipoEventoSelecionado = useMemo(() => {
    const first = (filtros.tipoEvento || [])[0];
    return first ? TIPO_EVENTO_DOMAIN.find((t) => t.id === first) : null;
  }, [filtros.tipoEvento]);

  return (
    <MainLayout>
      <MainCard
        title={
          <Box>
            <Typography variant="h2">Logs de Integração</Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Auditoria global de chamadas registradas (API Homefin e integrações bancárias)
            </Typography>
          </Box>
        }
        border={false}
        contentSX={{ pl: 1, pr: 1 }}
      >
        <Stack spacing={2}>
          {/* Filtros */}
          <FormCard title={'FILTROS'} border>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="dataInicioFrom">De</InputLabel>
                  <TextField
                    id="dataInicioFrom"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={filtros.dataInicioFrom ?? ''}
                    onChange={handleAlterar('dataInicioFrom')}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="dataInicioTo">Até</InputLabel>
                  <TextField
                    id="dataInicioTo"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={filtros.dataInicioTo ?? ''}
                    onChange={handleAlterar('dataInicioTo')}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="idOportunidade">ID Oportunidade</InputLabel>
                  <TextField
                    id="idOportunidade"
                    type="number"
                    size="small"
                    fullWidth
                    placeholder="Ex: 16154"
                    value={filtros.idOportunidade ?? ''}
                    onChange={handleAlterar('idOportunidade')}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="idSimulacao">ID Simulação</InputLabel>
                  <TextField
                    id="idSimulacao"
                    type="number"
                    size="small"
                    fullWidth
                    placeholder="Ex: 4521"
                    value={filtros.idSimulacao ?? ''}
                    onChange={handleAlterar('idSimulacao')}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="situacao">Situação</InputLabel>
                  <Autocomplete
                    id="situacao"
                    name="situacao"
                    placeholder="Todas"
                    fullWidth
                    formProps={formProps}
                    labelField="nome"
                    idField="id"
                    domain={SITUACAO_DOMAIN}
                    onChange={onChangeSituacao}
                    values={situacaoSelecionada}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="codigoFluxo">Serviço</InputLabel>
                  <Autocomplete
                    id="codigoFluxo"
                    name="codigoFluxo"
                    placeholder="Todos"
                    fullWidth
                    formProps={formProps}
                    labelField="nome"
                    idField="id"
                    domain={FLUXO_DOMAIN}
                    onChange={onChangeFluxo}
                    values={fluxoSelecionado}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="tipoEvento">Origem</InputLabel>
                  <Autocomplete
                    id="tipoEvento"
                    name="tipoEvento"
                    placeholder="Todas"
                    fullWidth
                    formProps={formProps}
                    labelField="nome"
                    idField="id"
                    domain={TIPO_EVENTO_DOMAIN}
                    onChange={onChangeTipoEvento}
                    values={tipoEventoSelecionado}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ pt: { xs: 0, sm: '24px' } }}
                  justifyContent="flex-end"
                >
                  <Button
                    onClick={handleLimpar}
                    variant="outlined"
                    color="secondary"
                    startIcon={<ClearOutlined />}
                  >
                    Limpar
                  </Button>
                  <Button
                    onClick={handleBuscar}
                    variant="contained"
                    color="primary"
                    startIcon={<SearchOutlined />}
                    isLoading={lista.loading}
                  >
                    Buscar
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </FormCard>

          {/* Lista */}
          <FormCard
            title={'RESULTADOS'}
            border
            subtitle={
              !lista.buscado
                ? 'Defina os filtros e clique em Buscar.'
                : lista.total > 0
                ? `${lista.total} chamada${lista.total > 1 ? 's' : ''} encontrada${
                    lista.total > 1 ? 's' : ''
                  }`
                : 'Nenhuma chamada encontrada para os filtros.'
            }
            contentSX={{ p: 0 }}
          >
            <DataGrid
              rows={lista.data}
              columns={columns}
              getRowId={(row) => row.idLogAuditoria}
              loading={lista.loading}
              pageSize={lista.pageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              noLineText={
                !lista.buscado
                  ? 'Defina os filtros e clique em Buscar para listar chamadas.'
                  : 'Nenhuma chamada encontrada para os filtros.'
              }
              onRowClick={(params) =>
                dispatch(selecionarLog(params.row.idLogAuditoria))
              }
              paginationMode="server"
              sortingMode="server"
              filterMode="server"
              rowCount={lista.total}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) =>
                dispatch(
                  alterarPaginacao({
                    page: model.page + 1,
                    pageSize: model.pageSize
                  })
                )
              }
              sortModel={sortModel}
              onSortModelChange={(model) => {
                const item = model?.[0];
                if (item) {
                  dispatch(
                    alterarSort({
                      field: item.field,
                      direction: item.sort || 'desc'
                    })
                  );
                }
              }}
              initialState={{ density: 'compact' }}
            />
          </FormCard>

          {/* Detalhe */}
          <LogDetalhe
            data={detalhe.data}
            loading={detalhe.loading}
            idSelecionado={detalhe.idSelecionado}
            emptyHint={
              lista.data.length > 0
                ? 'Clique em uma linha acima para inspecionar o conteúdo da requisição e da resposta.'
                : 'Os detalhes serão exibidos aqui quando você selecionar uma linha do resultado.'
            }
          />
        </Stack>
      </MainCard>
    </MainLayout>
  );
};

export default LogsIntegracaoPage;
